import type { OrgEventChecklistItemDef } from 'hooks/orgEvents/orgEventAutomationHooks';
import type { UserNotificationWithDetails } from 'hooks/useUserNotifications';
import type { TropicalDemoKind } from 'utils/featureFlags';
import type { MemphisRegionalThreats } from './memphisThreatNotification';
import {
  DHS_REGION4_ASSETS_LAYER_ID,
  DHS_REGION4_AUTOMATION_RULE_NAME,
  DHS_REGION4_FACILITIES,
  DHS_REGION4_LINKED_EVENT_NAME,
  DHS_REGION4_ORG_EVENT_DEMO_KEY,
  DHS_REGION4_RESOURCE_NOTES_PREFIX,
  DHS_RESPONSE_CHECKLIST_ITEMS,
  dhsFacilityIdFromResourceNotes,
  dhsRegion4ResourceNotesMarker,
  formatDhsFacilityLabel,
  getDhsRegion4ThreatNotification,
  getDhsRegionalThreats,
  isDhsRegion4OrgActionName,
  isDhsRegion4ResourceSet,
  isHardcodedDhsRegion4ThreatNotification,
  DHS_REGION4_THREAT_NOTIFICATION_ID,
} from './dhsRegion4ThreatNotification';
import {
  ONTARIO_ASSET_COA_NOTIFY_STORAGE_KEY,
  ONTARIO_GTA_ASSETS_LAYER_ID,
  ONTARIO_GTA_AUTOMATION_RULE_NAME,
  ONTARIO_GTA_FACILITIES,
  ONTARIO_GTA_LINKED_EVENT_NAME,
  ONTARIO_GTA_ORG_EVENT_DEMO_KEY,
  ONTARIO_GTA_RESOURCE_NOTES_PREFIX,
  ONTARIO_GTA_THREAT_NOTIFICATION_ID,
  ONTARIO_RESPONSE_CHECKLIST_ITEMS,
  formatOntarioFacilityLabel,
  getOntarioGtaThreatNotification,
  getOntarioRegionalThreats,
  isHardcodedOntarioGtaThreatNotification,
  isOntarioGtaOrgActionName,
  isOntarioGtaResourceSet,
  ontarioFacilityIdFromResourceNotes,
  ontarioGtaResourceNotesMarker,
} from './ontarioSnowstormNotification';
import {
  CNIC_HAWAII_ASSETS_LAYER_ID,
  CNIC_HAWAII_AUTOMATION_RULE_NAME,
  CNIC_HAWAII_FACILITIES,
  CNIC_HAWAII_LINKED_EVENT_NAME,
  CNIC_HAWAII_ORG_EVENT_DEMO_KEY,
  CNIC_HAWAII_RESOURCE_NOTES_PREFIX,
  CNIC_HAWAII_THREAT_NOTIFICATION_ID,
  CNIC_RESPONSE_CHECKLIST_ITEMS,
  cnicFacilityIdFromResourceNotes,
  cnicHawaiiResourceNotesMarker,
  formatCnicFacilityLabel,
  getCnicHawaiiThreatNotification,
  getCnicRegionalThreats,
  isCnicHawaiiOrgActionName,
  isCnicHawaiiResourceSet,
  isHardcodedCnicHawaiiThreatNotification,
} from './cnicHawaiiThreatNotification';
import {
  EQUINOR_ASSET_COA_NOTIFY_STORAGE_KEY,
  EQUINOR_GRANE_ASSETS_LAYER_ID,
  EQUINOR_GRANE_FACILITIES,
  EQUINOR_GRANE_LINKED_EVENT_NAME,
  EQUINOR_GRANE_ORG_EVENT_DEMO_KEY,
  EQUINOR_GRANE_RESOURCE_NOTES_PREFIX,
  EQUINOR_GRANE_SPILL_RULE_NAME,
  EQUINOR_GRANE_THREAT_NOTIFICATION_ID,
  EQUINOR_SPILL_CHECKLIST_ITEMS,
  equinorFacilityIdFromResourceNotes,
  equinorGraneResourceNotesMarker,
  formatEquinorFacilityLabel,
  getEquinorGraneThreatNotification,
  getEquinorRegionalThreats,
  isEquinorGraneOrgActionName,
  isEquinorGraneResourceSet,
  isHardcodedEquinorGraneThreatNotification,
} from './equinorGraneSpillNotification';

/**
 * DHS facilities and Equinor sites are structurally identical, which is what
 * lets both demos share one render path.
 */
export type FacilityDemoFacility = {
  id: string;
  name: string;
  city: string;
  state: string;
  lon: number;
  lat: number;
};

type AssetNotifyTemplates = {
  titleTemplate: string;
  bodyTemplate: string;
  emailSubject: string;
  emailMessage: string;
  appMessage: string;
};

/**
 * Everything the shared components need to render a facility-roster demo.
 *
 * Seeding hooks deliberately stay outside this: each demo posts its own event,
 * resources and rules, and those payloads have little in common beyond shape.
 */
export type FacilityDemoPack = {
  kind: 'dhs' | 'equinor' | 'ontario' | 'cnic';
  /** Short name for seeding status text and log lines. */
  label: string;
  notificationId: string;
  getNotification: (groupId: string) => UserNotificationWithDetails;
  isHardcodedNotification: (id: string | null | undefined) => boolean;
  getRegionalThreats: () => MemphisRegionalThreats;
  facilities: FacilityDemoFacility[];
  formatFacilityLabel: (facility: FacilityDemoFacility) => string;
  assetsLayerId: string;
  /** Rule that marks a resource as belonging to this demo's event. */
  primaryAutomationRuleName: string;
  resourceNotesPrefix: string;
  resourceNotesMarker: (facilityId: string) => string;
  facilityIdFromResourceNotes: (
    notes: string | null | undefined
  ) => string | undefined;
  isResourceSet: (resource: { notes?: string | null }) => boolean;
  orgEventDemoKey: string;
  linkedEventName: string;
  responseChecklistItems: OrgEventChecklistItemDef[];
  isOrgActionName: (name: string | null | undefined) => boolean;
  assetCoaNotifyStorageKey: string;
  assetNotifyTemplates: (assetLabel: string) => AssetNotifyTemplates;
  assetOrgActionDescription: (facilityId: string) => string;
};

const DHS_PACK: FacilityDemoPack = {
  kind: 'dhs',
  label: 'DHS',
  notificationId: DHS_REGION4_THREAT_NOTIFICATION_ID,
  getNotification: getDhsRegion4ThreatNotification,
  isHardcodedNotification: isHardcodedDhsRegion4ThreatNotification,
  getRegionalThreats: getDhsRegionalThreats,
  facilities: DHS_REGION4_FACILITIES,
  formatFacilityLabel: formatDhsFacilityLabel,
  assetsLayerId: DHS_REGION4_ASSETS_LAYER_ID,
  primaryAutomationRuleName: DHS_REGION4_AUTOMATION_RULE_NAME,
  resourceNotesPrefix: DHS_REGION4_RESOURCE_NOTES_PREFIX,
  resourceNotesMarker: dhsRegion4ResourceNotesMarker,
  facilityIdFromResourceNotes: dhsFacilityIdFromResourceNotes,
  isResourceSet: isDhsRegion4ResourceSet,
  orgEventDemoKey: DHS_REGION4_ORG_EVENT_DEMO_KEY,
  linkedEventName: DHS_REGION4_LINKED_EVENT_NAME,
  responseChecklistItems: DHS_RESPONSE_CHECKLIST_ITEMS,
  isOrgActionName: isDhsRegion4OrgActionName,
  assetCoaNotifyStorageKey: 'dhs-region4-asset-coa-notify-v1',
  assetNotifyTemplates: (assetLabel) => ({
    titleTemplate: `Accountability check: ${assetLabel} in {eventName} cone`,
    bodyTemplate: `Confirm personnel accountability for ${assetLabel} inside the {eventName} forecast cone. Report status in PRATUS.`,
    emailSubject: `Accountability check: ${assetLabel} — {eventName}`,
    emailMessage: `Confirm personnel accountability for ${assetLabel} inside the {eventName} forecast cone. Review Courses of Action and assets at risk in PRATUS.`,
    appMessage: `Accountability check: {eventName} — confirm personnel status for ${assetLabel}.`,
  }),
  assetOrgActionDescription: (facilityId) =>
    `DHS Region 4 demo — per-asset COA for ${dhsRegion4ResourceNotesMarker(facilityId)}`,
};

const ONTARIO_PACK: FacilityDemoPack = {
  kind: 'ontario',
  label: 'Government of Ontario',
  notificationId: ONTARIO_GTA_THREAT_NOTIFICATION_ID,
  getNotification: getOntarioGtaThreatNotification,
  isHardcodedNotification: isHardcodedOntarioGtaThreatNotification,
  getRegionalThreats: getOntarioRegionalThreats,
  facilities: ONTARIO_GTA_FACILITIES,
  formatFacilityLabel: formatOntarioFacilityLabel,
  assetsLayerId: ONTARIO_GTA_ASSETS_LAYER_ID,
  primaryAutomationRuleName: ONTARIO_GTA_AUTOMATION_RULE_NAME,
  resourceNotesPrefix: ONTARIO_GTA_RESOURCE_NOTES_PREFIX,
  resourceNotesMarker: ontarioGtaResourceNotesMarker,
  facilityIdFromResourceNotes: ontarioFacilityIdFromResourceNotes,
  isResourceSet: isOntarioGtaResourceSet,
  orgEventDemoKey: ONTARIO_GTA_ORG_EVENT_DEMO_KEY,
  linkedEventName: ONTARIO_GTA_LINKED_EVENT_NAME,
  responseChecklistItems: ONTARIO_RESPONSE_CHECKLIST_ITEMS,
  isOrgActionName: isOntarioGtaOrgActionName,
  assetCoaNotifyStorageKey: ONTARIO_ASSET_COA_NOTIFY_STORAGE_KEY,
  assetNotifyTemplates: (assetLabel) => ({
    titleTemplate: `Accountability check: ${assetLabel} in {eventName} path`,
    bodyTemplate: `Confirm personnel accountability for ${assetLabel} inside the {eventName} warning area. Report status in PRATUS.`,
    emailSubject: `Accountability check: ${assetLabel} — {eventName}`,
    emailMessage: `Confirm personnel accountability for ${assetLabel} inside the {eventName} warning area. Review Courses of Action and assets at risk in PRATUS.`,
    appMessage: `Accountability check: {eventName} — confirm personnel status for ${assetLabel}.`,
  }),
  assetOrgActionDescription: (facilityId) =>
    `Government of Ontario demo — per-asset COA for ${ontarioGtaResourceNotesMarker(facilityId)}`,
};

const CNIC_PACK: FacilityDemoPack = {
  kind: 'cnic',
  label: 'CNIC',
  notificationId: CNIC_HAWAII_THREAT_NOTIFICATION_ID,
  getNotification: getCnicHawaiiThreatNotification,
  isHardcodedNotification: isHardcodedCnicHawaiiThreatNotification,
  getRegionalThreats: getCnicRegionalThreats,
  facilities: CNIC_HAWAII_FACILITIES,
  formatFacilityLabel: formatCnicFacilityLabel,
  assetsLayerId: CNIC_HAWAII_ASSETS_LAYER_ID,
  primaryAutomationRuleName: CNIC_HAWAII_AUTOMATION_RULE_NAME,
  resourceNotesPrefix: CNIC_HAWAII_RESOURCE_NOTES_PREFIX,
  resourceNotesMarker: cnicHawaiiResourceNotesMarker,
  facilityIdFromResourceNotes: cnicFacilityIdFromResourceNotes,
  isResourceSet: isCnicHawaiiResourceSet,
  orgEventDemoKey: CNIC_HAWAII_ORG_EVENT_DEMO_KEY,
  linkedEventName: CNIC_HAWAII_LINKED_EVENT_NAME,
  responseChecklistItems: CNIC_RESPONSE_CHECKLIST_ITEMS,
  isOrgActionName: isCnicHawaiiOrgActionName,
  assetCoaNotifyStorageKey: 'cnic-hawaii-asset-coa-notify-v1',
  assetNotifyTemplates: (assetLabel) => ({
    titleTemplate: `Accountability check: ${assetLabel} in {eventName} cone`,
    bodyTemplate: `Confirm personnel accountability for ${assetLabel} inside the {eventName} forecast cone. Report status in PRATUS.`,
    emailSubject: `Accountability check: ${assetLabel} — {eventName}`,
    emailMessage: `Confirm personnel accountability for ${assetLabel} inside the {eventName} forecast cone. Review Courses of Action and assets at risk in PRATUS.`,
    appMessage: `Accountability check: {eventName} — confirm personnel status for ${assetLabel}.`,
  }),
  assetOrgActionDescription: (facilityId) =>
    `CNIC Hawaii demo — per-asset COA for ${cnicHawaiiResourceNotesMarker(facilityId)}`,
};

const EQUINOR_PACK: FacilityDemoPack = {
  kind: 'equinor',
  label: 'Equinor',
  notificationId: EQUINOR_GRANE_THREAT_NOTIFICATION_ID,
  getNotification: getEquinorGraneThreatNotification,
  isHardcodedNotification: isHardcodedEquinorGraneThreatNotification,
  getRegionalThreats: getEquinorRegionalThreats,
  facilities: EQUINOR_GRANE_FACILITIES,
  formatFacilityLabel: formatEquinorFacilityLabel,
  assetsLayerId: EQUINOR_GRANE_ASSETS_LAYER_ID,
  primaryAutomationRuleName: EQUINOR_GRANE_SPILL_RULE_NAME,
  resourceNotesPrefix: EQUINOR_GRANE_RESOURCE_NOTES_PREFIX,
  resourceNotesMarker: equinorGraneResourceNotesMarker,
  facilityIdFromResourceNotes: equinorFacilityIdFromResourceNotes,
  isResourceSet: isEquinorGraneResourceSet,
  orgEventDemoKey: EQUINOR_GRANE_ORG_EVENT_DEMO_KEY,
  linkedEventName: EQUINOR_GRANE_LINKED_EVENT_NAME,
  responseChecklistItems: EQUINOR_SPILL_CHECKLIST_ITEMS,
  isOrgActionName: isEquinorGraneOrgActionName,
  assetCoaNotifyStorageKey: EQUINOR_ASSET_COA_NOTIFY_STORAGE_KEY,
  assetNotifyTemplates: (assetLabel) => ({
    titleTemplate: `Accountability check: ${assetLabel} — {eventName}`,
    bodyTemplate: `Confirm personnel accountability and site status for ${assetLabel} under {eventName}. Report status in PRATUS.`,
    emailSubject: `Accountability check: ${assetLabel} — {eventName}`,
    emailMessage: `Confirm personnel accountability and site status for ${assetLabel}. Review Courses of Action and impacted assets in PRATUS.`,
    appMessage: `Accountability check: {eventName} — confirm status for ${assetLabel}.`,
  }),
  assetOrgActionDescription: (facilityId) =>
    `Equinor Grane demo — per-asset COA for ${equinorGraneResourceNotesMarker(facilityId)}`,
};

export const FACILITY_DEMO_PACKS = {
  dhs: DHS_PACK,
  equinor: EQUINOR_PACK,
  ontario: ONTARIO_PACK,
  cnic: CNIC_PACK,
} as const;

/** Null for EPA / CFA / no demo, which do not use the facility roster layout. */
export function getFacilityDemoPack(
  kind: TropicalDemoKind
): FacilityDemoPack | null {
  if (kind === 'dhs') return DHS_PACK;
  if (kind === 'equinor') return EQUINOR_PACK;
  if (kind === 'ontario') return ONTARIO_PACK;
  if (kind === 'cnic') return CNIC_PACK;
  return null;
}

/**
 * Looks a site up across every facility pack, for call sites that have an asset
 * id but no demo kind in scope. Ids are namespaced per demo, so this cannot
 * collide.
 */
export function facilityIdFromAnyFacilityDemoNotes(
  notes: string | null | undefined
): string | undefined {
  for (const pack of Object.values(FACILITY_DEMO_PACKS)) {
    const id = pack.facilityIdFromResourceNotes(notes);
    if (id) return id;
  }
  return undefined;
}

export function findAnyFacilityDemoSite(
  siteId: string | undefined
): FacilityDemoFacility | undefined {
  if (!siteId) return undefined;
  for (const pack of Object.values(FACILITY_DEMO_PACKS)) {
    const match = pack.facilities.find((entry) => entry.id === siteId);
    if (match) return match;
  }
  return undefined;
}

/** Matches a hardcoded demo notification id against every facility pack. */
export function isFacilityDemoNotificationId(
  id: string | null | undefined
): boolean {
  return Object.values(FACILITY_DEMO_PACKS).some((pack) =>
    pack.isHardcodedNotification(id)
  );
}
